import { MFAFactor, MFAFactorCreateRequest, MFAFactorType, MFAModalPageIdentifier, SMSMFAFactorUpdateRequest, TOTPMFAFactor } from '../../models/MFA'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'

interface MFAState {
  flow: 'login' | 'create' | 'update' | 'delete'
  pageIdentifier: MFAModalPageIdentifier
  factor: MFAFactor | null
  createRequest: MFAFactorCreateRequest | null
  factorToUpdate: MFAFactor | null
  updateRequest: SMSMFAFactorUpdateRequest | null
  factorIdToDelete: string | null
  recoveryCodes: string[]
}

const initialState: MFAState = {
  flow: 'login',
  pageIdentifier: MFAModalPageIdentifier.LOADING,
  factor: null,
  createRequest: null,
  factorToUpdate: null,
  updateRequest: null,
  factorIdToDelete: null,
  recoveryCodes: [],
}

interface MutationFlowRequest {
  factorToChallenge: MFAFactor
}

export interface CreateMFAFlowRequest extends MutationFlowRequest {
  createRequest: MFAFactorCreateRequest
}

export interface UpdateMFAFlowRequest extends MutationFlowRequest {
  updateRequest: SMSMFAFactorUpdateRequest
}

export interface DeleteMFAFlowRequest extends MutationFlowRequest {
  factorId: string
}

export const mfaSlice = createSlice({
  name: 'mfa',
  initialState,
  reducers: {
    resetFlow: () => initialState,
    setupMFAForLogin: (state) => {
      state.flow = 'login'
      state.factor = null
      state.recoveryCodes = []
      state.pageIdentifier = MFAModalPageIdentifier.SETUP
    },
    createMFA: (state, action: PayloadAction<MFAFactorType>) => {
      state.flow = 'create'
      state.factor = null
      state.recoveryCodes = []
      state.createRequest = null
      state.pageIdentifier = action.payload === 'sms' ? MFAModalPageIdentifier.SETUP_SMS : MFAModalPageIdentifier.SETUP_TOTP
    },
    updateMFA: (state, action: PayloadAction<MFAFactor>) => {
      state.flow = 'update'
      state.factor = null
      state.factorToUpdate = action.payload
      state.updateRequest = null
      // TODO: create update modal and replace the setup page here
      state.pageIdentifier = MFAModalPageIdentifier.SETUP_SMS
    },
    deleteMFA: (state, action: PayloadAction<string>) => {
      state.flow = 'delete'
      state.factor = null
      state.factorIdToDelete = action.payload
      state.pageIdentifier = MFAModalPageIdentifier.DELETE_FACTOR
    },
    toSetupSMSPage: (state) => {
      state.factor = null
      state.recoveryCodes = []
      state.pageIdentifier = MFAModalPageIdentifier.SETUP_SMS
    },
    toSetupFactorPage: (state, action: PayloadAction<MFAFactor>) => {
      state.factor = action.payload
      state.pageIdentifier = action.payload.type === 'sms' ? MFAModalPageIdentifier.SETUP_SMS_CODE : MFAModalPageIdentifier.SETUP_TOTP_SCAN
    },
    toSetupTOTPPage: (state) => {
      state.factor = null
      state.recoveryCodes = []
      state.pageIdentifier = MFAModalPageIdentifier.SETUP_TOTP
    },
    toSetupTOTPCodePage: (state, action: PayloadAction<TOTPMFAFactor>) => {
      state.factor = action.payload
      state.recoveryCodes = []
      state.pageIdentifier = MFAModalPageIdentifier.SETUP_TOTP_CODE
    },
    toSetupCompletePage: (state, action: PayloadAction<string[]>) => {
      state.recoveryCodes = action.payload
      state.pageIdentifier = MFAModalPageIdentifier.SETUP_COMPLETE
    },
    toClaimMFAChoicePage: (state) => {
      state.factor = null
      state.pageIdentifier = MFAModalPageIdentifier.CLAIM_MFA_CHOICE
    },
    toClaimCodePage: (state, action: PayloadAction<MFAFactor>) => {
      state.factor = action.payload
      state.pageIdentifier = action.payload.type === 'sms' ? MFAModalPageIdentifier.CLAIM_SMS_CODE : MFAModalPageIdentifier.CLAIM_TOTP
    },
    toClaimCodePageForCreate: (state, action: PayloadAction<CreateMFAFlowRequest>) => {
      const { createRequest, factorToChallenge } = action.payload
      state.flow = 'create'
      state.createRequest = createRequest
      state.factor = factorToChallenge
      state.pageIdentifier = MFAModalPageIdentifier.CREATE_FACTOR
    },
    toClaimRecoveryCodePage: (state) => {
      state.factor = null
      state.pageIdentifier = MFAModalPageIdentifier.CLAIM_RECOVERY_CODE
    },
  },
})

export const {
  resetFlow,
  setupMFAForLogin,
  createMFA,
  updateMFA,
  deleteMFA,
  toSetupSMSPage,
  toSetupFactorPage,
  toSetupTOTPPage,
  toSetupTOTPCodePage,
  toSetupCompletePage,
  toClaimMFAChoicePage,
  toClaimCodePage,
  toClaimCodePageForCreate,
  toClaimRecoveryCodePage,
} = mfaSlice.actions

export const selectMFAFlow = (state: RootState) => state.mfa.flow
export const selectPageIdentifier = (state: RootState) => state.mfa.pageIdentifier
export const selectMFAFactor = (state: RootState) => state.mfa.factor
export const selectRecoveryCodes = (state: RootState) => state.mfa.recoveryCodes
export const selectCreateRequest = (state: RootState) => state.mfa.createRequest
export const selectFactorToUpdate = (state: RootState) => state.mfa.factorToUpdate
export const selectUpdateRequest = (state: RootState) => state.mfa.updateRequest
export const selectFactorIdToDelete = (state: RootState) => state.mfa.factorIdToDelete

export default mfaSlice.reducer
